import { Helmet } from "react-helmet-async";
import { useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Globe, Languages, BarChart3, TrendingUp, BookOpen, Mic } from "lucide-react";
import Highcharts from 'highcharts';
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { Link } from "react-router-dom";

const LANGUAGE_NAMES: Record<string, string> = {
  es: 'Spanish', zh: 'Chinese', ja: 'Japanese', fr: 'French', de: 'German',
  pt: 'Portuguese', ko: 'Korean', ar: 'Arabic', ru: 'Russian', vi: 'Vietnamese',
  hi: 'Hindi', tl: 'Tagalog', it: 'Italian', pl: 'Polish', fa: 'Farsi',
  ur: 'Urdu', ht: 'Haitian Creole', so: 'Somali', my: 'Burmese'
};

const LANGUAGE_FLAGS: Record<string, string> = {
  es: '🇪🇸', zh: '🇨🇳', ja: '🇯🇵', fr: '🇫🇷', de: '🇩🇪', pt: '🇧🇷',
  ko: '🇰🇷', ar: '🇸🇦', ru: '🇷🇺', vi: '🇻🇳', hi: '🇮🇳', tl: '🇵🇭',
  it: '🇮🇹', pl: '🇵🇱', fa: '🇮🇷', ur: '🇵🇰', ht: '🇭🇹', so: '🇸🇴', my: '🇲🇲'
};

interface TranslationStats {
  total_scenarios: number;
  fully_translated: number;
  total_translation_rows: number;
  language_coverage: { language: string; scenarios_covered: number }[];
  daily_growth: { date: string; rows_added: number }[];
  narration: {
    scenarios_with_narration: number;
    total_en_lines: number;
    translated_lines: number;
  };
}

const TranslationDashboard = () => {
  const dailyGrowthRef = useRef<HTMLDivElement>(null);
  const langCoverageRef = useRef<HTMLDivElement>(null);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAHP = brand.key === 'ahp';
  const isAHPDark = isAHP && isDark;

  const palette = isAHPDark ? {
    bg: 'bg-[#0a1628]',
    cardBg: 'bg-[#0d1e35] border-[#FAF9F6]/10',
    headerText: 'text-[#FAF9F6]',
    subText: 'text-[#FAF9F6]/60',
    mutedText: 'text-[#FAF9F6]/45',
    accent1: '#5B9BD5', accent2: '#4ade80', accent3: '#C9A961', accent4: '#B22234',
    chartTitle: '#FAF9F6', chartSubtitle: 'rgba(250,249,246,0.55)',
    chartLabel: 'rgba(250,249,246,0.55)', chartGrid: 'rgba(250,249,246,0.1)',
    tooltipBg: '#0d1e35', tooltipBorder: '#5B9BD5', tooltipText: '#FAF9F6',
    badgeGood: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    badgeNeutral: 'bg-[#5B9BD5]/15 text-[#5B9BD5] border-[#5B9BD5]/30',
    progressBg: 'bg-[#5B9BD5]/10', progressFill: 'bg-[#5B9BD5]',
    langGood: 'border-emerald-500/40 bg-emerald-500/5',
    langMid: 'border-[#C9A961]/40 bg-[#C9A961]/5',
    langLow: 'border-[#5B9BD5]/40 bg-[#5B9BD5]/5',
  } : isAHP ? {
    bg: 'bg-[#F7F4EF]',
    cardBg: 'bg-white/90 border-[#1a3a6b]/20 shadow-sm',
    headerText: 'text-[#1a3a6b]',
    subText: 'text-[#4a5568]',
    mutedText: 'text-[#718096]',
    accent1: '#1a3a6b', accent2: '#2b5e49', accent3: '#C9A961', accent4: '#B22234',
    chartTitle: '#1a3a6b', chartSubtitle: '#4a5568',
    chartLabel: '#4a5568', chartGrid: '#e2e8f0',
    tooltipBg: '#ffffff', tooltipBorder: '#1a3a6b', tooltipText: '#1a3a6b',
    badgeGood: 'bg-[#2b5e49]/10 text-[#2b5e49] border-[#2b5e49]/30',
    badgeNeutral: 'bg-[#1a3a6b]/10 text-[#1a3a6b] border-[#1a3a6b]/30',
    progressBg: 'bg-[#1a3a6b]/10', progressFill: 'bg-[#1a3a6b]',
    langGood: 'border-[#2b5e49]/50 bg-[#2b5e49]/5',
    langMid: 'border-[#C9A961]/50 bg-[#C9A961]/5',
    langLow: 'border-[#1a3a6b]/50 bg-[#1a3a6b]/5',
  } : {
    bg: 'bg-background',
    cardBg: 'bg-black/40 border-muted',
    headerText: 'text-matrix-green font-mono',
    subText: 'text-muted-foreground',
    mutedText: 'text-muted-foreground',
    accent1: '#10b981', accent2: '#3b82f6', accent3: '#f59e0b', accent4: '#8b5cf6',
    chartTitle: '#e2e8f0', chartSubtitle: '#94a3b8',
    chartLabel: '#94a3b8', chartGrid: '#374151',
    tooltipBg: '#1f2937', tooltipBorder: '#374151', tooltipText: '#e2e8f0',
    badgeGood: 'bg-green-500/20 text-green-400',
    badgeNeutral: '',
    progressBg: 'bg-green-500/10', progressFill: 'bg-green-500',
    langGood: 'border-green-500/50 bg-green-500/10',
    langMid: 'border-yellow-500/50 bg-yellow-500/10',
    langLow: 'border-blue-500/50 bg-blue-500/10',
  };

  const { data: stats, isLoading } = useQuery<TranslationStats>({
    queryKey: ['translation-dashboard-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_translation_dashboard_stats');
      if (error) throw error;
      return data as unknown as TranslationStats;
    },
    refetchInterval: 60000,
  });

  // Daily growth chart
  useEffect(() => {
    if (!stats?.daily_growth || !dailyGrowthRef.current) return;
    const days = stats.daily_growth;
    // Build cumulative
    let cumulative = 0;
    const cumData = days.map(d => {
      cumulative += d.rows_added;
      return cumulative;
    });
    Highcharts.chart(dailyGrowthRef.current, {
      chart: { type: 'column', backgroundColor: 'transparent', height: 280 },
      title: { text: 'Translation Rows Added Per Day', style: { color: palette.chartTitle, fontSize: '14px' } },
      xAxis: { categories: days.map(d => d.date.slice(5)), labels: { style: { color: palette.chartLabel, fontSize: '10px' } }, gridLineColor: palette.chartGrid },
      yAxis: [
        { title: { text: 'Rows Added', style: { color: palette.chartLabel } }, labels: { style: { color: palette.chartLabel } }, gridLineColor: palette.chartGrid },
        { title: { text: 'Cumulative', style: { color: palette.chartLabel } }, labels: { style: { color: palette.chartLabel } }, opposite: true, gridLineColor: 'transparent' }
      ],
      tooltip: { backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, style: { color: palette.tooltipText } },
      legend: { itemStyle: { color: palette.chartLabel } },
      series: [
        { name: 'Daily', type: 'column', data: days.map(d => d.rows_added), color: palette.accent1 },
        { name: 'Cumulative', type: 'spline', data: cumData, yAxis: 1, color: palette.accent3, marker: { enabled: false } }
      ],
      credits: { enabled: false },
    });
  }, [stats, palette]);

  // Language coverage chart
  useEffect(() => {
    if (!stats?.language_coverage || !langCoverageRef.current) return;
    const langs = stats.language_coverage;
    const total = stats.total_scenarios;
    Highcharts.chart(langCoverageRef.current, {
      chart: { type: 'bar', backgroundColor: 'transparent', height: Math.max(400, langs.length * 28) },
      title: { text: 'Scenarios Translated Per Language', style: { color: palette.chartTitle, fontSize: '14px' } },
      xAxis: { categories: langs.map(l => `${LANGUAGE_FLAGS[l.language] || ''} ${LANGUAGE_NAMES[l.language] || l.language}`), labels: { style: { color: palette.chartLabel, fontSize: '11px' } } },
      yAxis: { max: total, title: { text: '' }, labels: { style: { color: palette.chartLabel } }, gridLineColor: palette.chartGrid, plotLines: [{ value: total, color: palette.accent3, width: 2, dashStyle: 'Dash', label: { text: `Goal: ${total.toLocaleString()}`, style: { color: palette.chartLabel, fontSize: '10px' } } }] },
      tooltip: {
        backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, style: { color: palette.tooltipText },
        formatter: function(this: Highcharts.TooltipFormatterContextObject) { return `<b>${this.x}</b><br/>Translated: ${(this.y ?? 0).toLocaleString()} / ${total.toLocaleString()} (${((this.y ?? 0)/total*100).toFixed(1)}%)`; }
      },
      plotOptions: { bar: { borderRadius: 3 } },
      series: [{ name: 'Scenarios', type: 'bar', data: langs.map(l => l.scenarios_covered), color: palette.accent1, showInLegend: false }],
      credits: { enabled: false },
      legend: { enabled: false },
    });
  }, [stats, palette]);

  if (isLoading || !stats) {
    return <div className={`min-h-screen ${palette.bg} flex items-center justify-center`}><p className={palette.subText}>Loading translation stats...</p></div>;
  }

  const scenarioPct = (stats.fully_translated / stats.total_scenarios * 100);
  const totalPossible = stats.total_scenarios * 19 * 5;
  const overallPct = (stats.total_translation_rows / totalPossible * 100);
  const narrationPossible = stats.narration.total_en_lines * 19;
  const narrationPct = narrationPossible > 0 ? (stats.narration.translated_lines / narrationPossible * 100) : 0;
  const remaining = stats.total_scenarios - stats.fully_translated;
  const todayGrowth = stats.daily_growth?.[stats.daily_growth.length - 1]?.rows_added || 0;

  return (
    <>
      <Helmet><title>Translation Dashboard | American Hospital Prices</title></Helmet>
      <div className={`min-h-screen ${palette.bg} p-4 md:p-8`}>
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className={`text-2xl md:text-3xl font-bold ${palette.headerText} flex items-center gap-3`}>
                <Globe className="w-7 h-7" /> Translation Dashboard
              </h1>
              <p className={`${palette.subText} mt-1`}>
                {stats.total_scenarios.toLocaleString()} medical scenarios across 19 languages
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/ingestion-dashboard">
                <Badge className={palette.badgeNeutral}>Ingestion Dashboard</Badge>
              </Link>
              <Badge className={palette.badgeGood}>{todayGrowth.toLocaleString()} rows today</Badge>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className={`w-4 h-4 ${palette.subText}`} />
                  <span className={`text-xs ${palette.mutedText}`}>Total Scenarios</span>
                </div>
                <div className={`text-2xl font-bold ${palette.headerText}`}>{stats.total_scenarios.toLocaleString()}</div>
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Languages className={`w-4 h-4 ${palette.subText}`} />
                  <span className={`text-xs ${palette.mutedText}`}>Fully Translated</span>
                </div>
                <div className={`text-2xl font-bold ${palette.headerText}`}>{stats.fully_translated.toLocaleString()}</div>
                <div className={`text-xs ${palette.mutedText}`}>{scenarioPct.toFixed(1)}% complete</div>
                <Progress value={scenarioPct} className={`mt-2 h-1.5 ${palette.progressBg}`} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className={`w-4 h-4 ${palette.subText}`} />
                  <span className={`text-xs ${palette.mutedText}`}>Translation Rows</span>
                </div>
                <div className={`text-2xl font-bold ${palette.headerText}`}>{stats.total_translation_rows.toLocaleString()}</div>
                <div className={`text-xs ${palette.mutedText}`}>{overallPct.toFixed(1)}% of {totalPossible.toLocaleString()}</div>
                <Progress value={overallPct} className={`mt-2 h-1.5 ${palette.progressBg}`} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className={`w-4 h-4 ${palette.subText}`} />
                  <span className={`text-xs ${palette.mutedText}`}>Remaining</span>
                </div>
                <div className={`text-2xl font-bold ${palette.headerText}`}>{remaining.toLocaleString()}</div>
                <div className={`text-xs ${palette.mutedText}`}>scenarios need translation</div>
              </CardContent>
            </Card>
          </div>

          {/* Narration Stats */}
          <Card className={palette.cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${palette.headerText} flex items-center gap-2`}>
                <Mic className="w-4 h-4" /> Dignity Narration Translation
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className={`text-xl font-bold ${palette.headerText}`}>{stats.narration.scenarios_with_narration.toLocaleString()}</div>
                  <div className={`text-xs ${palette.mutedText}`}>Scenarios with narration</div>
                </div>
                <div>
                  <div className={`text-xl font-bold ${palette.headerText}`}>{stats.narration.total_en_lines.toLocaleString()}</div>
                  <div className={`text-xs ${palette.mutedText}`}>English source lines</div>
                </div>
                <div>
                  <div className={`text-xl font-bold ${palette.headerText}`}>{stats.narration.translated_lines.toLocaleString()}</div>
                  <div className={`text-xs ${palette.mutedText}`}>Translated lines ({narrationPct.toFixed(1)}%)</div>
                </div>
              </div>
              <Progress value={narrationPct} className={`mt-3 h-1.5 ${palette.progressBg}`} />
            </CardContent>
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div ref={dailyGrowthRef} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-4">
                <div ref={langCoverageRef} />
              </CardContent>
            </Card>
          </div>

          {/* Language Breakdown Table */}
          <Card className={palette.cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${palette.headerText}`}>Language Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {stats.language_coverage.map(lang => {
                  const pct = (lang.scenarios_covered / stats.total_scenarios * 100);
                  const tierClass = pct >= 80 ? palette.langGood : pct >= 10 ? palette.langMid : palette.langLow;
                  return (
                    <div key={lang.language} className={`border rounded-lg p-3 ${tierClass}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${palette.headerText}`}>
                          {LANGUAGE_FLAGS[lang.language] || ''} {LANGUAGE_NAMES[lang.language] || lang.language}
                        </span>
                        <span className={`text-xs font-mono ${palette.mutedText}`}>{pct.toFixed(1)}%</span>
                      </div>
                      <div className={`text-xs ${palette.mutedText}`}>
                        {lang.scenarios_covered.toLocaleString()} / {stats.total_scenarios.toLocaleString()}
                      </div>
                      <Progress value={pct} className={`mt-1 h-1 ${palette.progressBg}`} />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </>
  );
};

export default TranslationDashboard;
